import {useState, useEffect} from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

export default function SsoCallback() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('processing'); // processing, success, error
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        handleCallback();
    }, []);

    async function handleCallback() {
        // Microsoft OAuth returns these params:
        // Success: ?code={authorization_code}&state={state}
        // Error: ?error={error}&error_description={desc}&state={state}
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // Handle OAuth errors from Microsoft
        if (error) {
            setStatus('error');
            setErrorMessage(getErrorMessage(error, errorDescription));
            return;
        }

        // Validate we have the authorization code and state
        if (!code || !state) {
            setStatus('error');
            setErrorMessage('Missing authorization code or state from Microsoft.');
            return;
        }

        try {
            // Exchange code for token (state is validated server-side)
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/sso/callback`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        code: code,
                        state: state
                    })
                }
            );

            const result = await response.json();

            if (response.ok) {
                setStatus('success');

                // Store token and role
                localStorage.setItem('apiToken', result.token);
                localStorage.setItem('role', result.role);

                // Clean up any stored SSO email
                sessionStorage.removeItem('sso_email');

                // Redirect to appropriate page after brief success message
                setTimeout(() => {
                    if (result.role === 'admin') {
                        window.location.href = '/admin';
                    } else {
                        window.location.href = '/';
                    }
                }, 1000);
            } else {
                setStatus('error');
                setErrorMessage(result.message || 'SSO login failed.');
            }
        } catch (error) {
            setStatus('error');
            setErrorMessage('An error occurred during authentication. Please try again.');
        }
    }

    function getErrorMessage(error, description) {
        // Handle common OAuth errors with user-friendly messages
        switch (error) {
            case 'access_denied':
                return 'Access was denied. You may have cancelled the login or lack permission.';
            case 'login_required':
                return 'Login is required. Please try again.';
            case 'consent_required':
                return 'Admin consent is required for this application.';
            case 'interaction_required':
                return 'Additional interaction is required. Please try again.';
            case 'server_error':
                return 'Microsoft encountered an error. Please try again later.';
            case 'temporarily_unavailable':
                return 'Microsoft login is temporarily unavailable. Please try again later.';
            default:
                return description || 'Authentication failed. Please try again.';
        }
    }

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
                    Entra ID Sign-In
                </h2>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                    {status === 'processing' && (
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="mt-4 text-sm text-gray-600">Completing sign-in...</p>
                        </div>
                    )}

                    {status === 'success' && (
                        <div className="text-center">
                            <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto" />
                            <p className="mt-4 text-lg font-medium text-gray-900">Authentication Successful!</p>
                            <p className="mt-2 text-sm text-gray-600">Redirecting...</p>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="text-center">
                            <XCircleIcon className="h-12 w-12 text-red-500 mx-auto" />
                            <p className="mt-4 text-lg font-medium text-gray-900">Authentication Failed</p>
                            <p className="mt-2 text-sm text-red-600">{errorMessage}</p>
                            <button
                                onClick={() => navigate('/login')}
                                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                Return to Login
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
